const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

export const isVia40Express = (name) => normalize(name) === "VIA40EXPRESS";
export const distributionKeys = [
  "via40Gopass",
  "alosurGopass",
  "foseviGopass",
  "via40Concesion",
  "alosurConcesion",
  "foseviConcesion",
];
const numeric = (value) =>
  value == null || String(value).trim() === "" || !Number.isFinite(Number(value))
    ? null
    : Number(value);

const beneficiary = (peaje) =>
  ({
    VIA401: "via40",
    VIA402: "via40",
    VALORAPAGARALOSUR: "alosur",
    PAGOSEGURIDADVIAL: "fosevi",
  }[normalize(peaje)]);

export const distributeVia40 = (row) => {
  const peaje = normalize(row.peaje).replace(/^PEAJE/, "");
  const known = ["CHINAUTA", "CHUSACA"].includes(peaje);
  const result = {};
  const recipient = beneficiary(row.peaje);
  [
    ["Gopass", row.totalPasos, row.gopassPasosExitosos],
    ["Concesion", row.pasosConcesion, row.valorConcesion],
  ].forEach(([source, steps, value]) => {
    const count = numeric(steps);
    const amount = numeric(value);
    if (recipient) {
      // These rows already contain the beneficiary's net payment. Do not deduct FOSEVI again.
      const reported = source === "Gopass" || count != null;
      ["fosevi", "via40", "alosur"].forEach((name) => {
        result[`${name}${source}`] =
          reported && amount != null ? (name === recipient ? amount : 0) : null;
      });
      return;
    }
    const valid = known && count != null && count >= 0 && amount != null;
    const foseviCents = valid ? Math.round(count * 553 * 100) : null;
    const remainingCents = valid ? Math.round(amount * 100) - foseviCents : null;
    const via40Cents = valid
      ? peaje === "CHINAUTA"
        ? remainingCents
        : Math.round(remainingCents * 0.75)
      : null;
    result[`fosevi${source}`] = valid ? foseviCents / 100 : null;
    result[`via40${source}`] = valid ? via40Cents / 100 : null;
    result[`alosur${source}`] = valid ? (remainingCents - via40Cents) / 100 : null;
  });
  return result;
};

// Aggregate detail allocations, never redistribute a subtotal containing different tolls.
export const buildVia40Distribution = (rows) => {
  const totals = Object.fromEntries(distributionKeys.map((key) => [key, null]));
  const byDay = new Map();
  const decorated = rows.map((row) => {
    if (row.tipoFila === "subtotal" || row.tipoFila === "total") return row;
    const values = distributeVia40(row);
    const day =
      byDay.get(row.fecha) || Object.fromEntries(distributionKeys.map((key) => [key, null]));
    distributionKeys.forEach((key) => {
      if (values[key] == null) return;
      day[key] = Math.round(((day[key] ?? 0) + values[key]) * 100) / 100;
      totals[key] = Math.round(((totals[key] ?? 0) + values[key]) * 100) / 100;
    });
    byDay.set(row.fecha, day);
    return { ...row, ...values };
  });
  const detailDays = new Set(
    rows
      .filter((row) => row.tipoFila !== "subtotal" && row.tipoFila !== "total")
      .map((row) => row.fecha)
  );
  const dailyCounts = new Map(
    rows
      .filter((row) => row.tipoFila === "subtotal" && numeric(row.totalPasos) != null)
      .map((row) => [row.fecha, Number(row.totalPasos)])
  );
  const hasBeneficiaries = rows.some((row) => beneficiary(row.peaje));
  const totalPasos =
    hasBeneficiaries && detailDays.size > 0 && [...detailDays].every((day) => dailyCounts.has(day))
      ? [...detailDays].reduce((total, day) => total + dailyCounts.get(day), 0)
      : null;
  return {
    totals,
    totalPasos,
    rows: decorated.map((row) =>
      row.tipoFila === "subtotal"
        ? { ...row, ...byDay.get(row.fecha) }
        : row.tipoFila === "total"
        ? { ...row, ...totals }
        : row
    ),
  };
};

// Sum the daily detail amounts already calculated, excluding daily/general total rows.
export const summarizeVia40 = (rows, keys) => {
  const byPeaje = new Map();
  const totals = Object.fromEntries(keys.map((key) => [key, null]));
  rows.forEach((row) => {
    if (row.tipoFila && row.tipoFila !== "detalle") return;
    const name = normalize(row.peaje);
    const summary = byPeaje.get(name) || { peaje: row.peaje, ...totals };
    keys.forEach((key) => {
      const amount = numeric(row[key]);
      if (amount == null) return;
      summary[key] = Math.round(((summary[key] ?? 0) + amount) * 100) / 100;
    });
    byPeaje.set(name, summary);
  });
  const summaries = [...byPeaje.values()];
  keys.forEach((key) => {
    const values = summaries.map((row) => row[key]).filter((value) => value != null);
    totals[key] = values.length
      ? Math.round(values.reduce((sum, value) => sum + value, 0) * 100) / 100
      : null;
  });
  return { rows: summaries, totals: { peaje: "TOTALES", ...totals } };
};

export const via40Calculation = (row) => {
  const format = (value) =>
    numeric(value) == null ? "Sin información" : Number(value).toLocaleString("es-CO");
  const peaje = normalize(row.peaje).replace(/^PEAJE/, "");
  if (beneficiary(row.peaje)) {
    return `Importes ya distribuidos por el servidor para ${
      row.peaje
    }. Se acumulan los días del filtro sin volver a descontar FOSEVI ni aplicar porcentajes. GoPass: $${format(
      row.gopassPasosExitosos
    )}. ${
      row.foseviConcesion == null
        ? "Concesión: pendiente de datos reportados."
        : `Concesión: $${format(row.valorConcesion)}.`
    }`;
  }
  const sources = [
    `FOSEVI GoPass: ${format(row.totalPasos)} pasos × $553 = $${format(row.foseviGopass)}.`,
    row.foseviConcesion == null
      ? "Concesión: pendiente de datos reportados."
      : `FOSEVI concesión: ${format(row.pasosConcesion)} pasos × $553 = $${format(
          row.foseviConcesion
        )}.`,
  ];
  const rule =
    peaje === "CHINAUTA"
      ? "Valor de cada fuente menos su FOSEVI: 100% VÍA 40 1; VÍA ALÓSUR $0."
      : peaje === "CHUSACA"
      ? "Valor de cada fuente menos su FOSEVI: 75% VÍA 40 1 y 25% VÍA ALÓSUR."
      : "Suma de las distribuciones por peaje.";
  return [...sources, rule, "Importes acumulados de los días del filtro."].join(" ");
};
